<template>
  <WLBaseSelect
    :id="id"
    ref="baseSelectRef"
    v-model:is-open="isOpen"
    :type="type"
    :is-disabled="isDisabled"
    :is-full-width="isFullWidth"
    :label="label"
    :label-icon="labelIcon"
    :container-classes="containerClasses"
    :placement="placement"
  >
    <div
      ref="singleSelectRef"
      class="flex flex-col flex-nowrap items-start gap-1 p-1"
    >
      <WLSingleSelectOption
        v-for="({ label, sublabel, value, icon }, index) in options"
        :key="index"
        ref="optionRefs"
        class="font-display flex w-full items-center gap-x-1 rounded-lg px-2 py-1.5 text-start text-sm font-medium outline-none outline-0 hover:cursor-pointer hover:bg-muted focus-visible:outline-1 focus-visible:outline-accent"
        :class="[
          { 'is-active bg-muted hover:!bg-emphasis': modelValue === value },
          modelValue === value ? 'text-accent' : 'text-emphasis',
        ]"
        :auto-focus="modelValue === value"
        @keyup.enter="handleKeyUpEnter(value)"
        @keydown.up="handleArrowKeys(index - 1)"
        @keydown.down="handleArrowKeys(index + 1)"
        @keydown.tab="handleOptionTab"
        @click="handleOptionClick(value)"
      >
        <WLIcon v-if="icon" :name="icon" :size="IconSize.SMALL" class="mr-1" />
        <div class="flex grow flex-col">
          <div class="grow">
            {{ label }}
          </div>
          <div
            v-if="sublabel"
            ref="sublabelRef"
            class="text-xs text-muted"
            :class="{ active: modelValue == value }"
          >
            {{ sublabel }}
          </div>
        </div>
      </WLSingleSelectOption>
    </div>
  </WLBaseSelect>
</template>

<script lang="ts" setup generic="T">
import { computed, ref, toRefs, onBeforeUpdate } from "vue";
import { onClickOutside } from "@vueuse/core";
import WLBaseSelect from "./BaseSelect.vue";
import WLSingleSelectOption from "./SingleSelectOption.vue";
import { SelectType } from "./constants";
import { WLIcon, IconSize } from "../icon";

import { type WLSingleSelectProps } from "./types";

const props = withDefaults(defineProps<WLSingleSelectProps<T>>(), {
  type: SelectType.DEFAULT,
});

const {
  id,
  type,
  options,
  label,
  labelIcon,
  isFullWidth,
  isDisabled,
  containerClasses,
  placement,
} = toRefs(props);

const modelValue = defineModel<T>({ required: true });

const isOpen = ref(false);
const baseSelectRef = ref<InstanceType<typeof WLBaseSelect> | null>(null);
const buttonRef = computed(() => baseSelectRef?.value?.buttonRef);
const singleSelectRef = ref<HTMLElement | null>(null);
const optionRefs = ref<Array<typeof WLSingleSelectOption>>([]);

const handleOptionTab = (): void => {
  if (isOpen.value) {
    isOpen.value = false;
    baseSelectRef?.value?.buttonRef?.focus();
  }
};

const handleOptionClick = (value: T): void => {
  isOpen.value = false;
  modelValue.value = value;
};

const handleKeyUpEnter = (value: T): void => {
  modelValue.value = value;
};

const handleArrowKeys = (targetIndex: number): void => {
  if (optionRefs.value?.length) {
    const option = optionRefs.value[targetIndex];

    if (option) {
      option?.buttonRef?.focus();
      return;
    }

    if (targetIndex < 0) {
      buttonRef?.value?.focus();
    }

    isOpen.value = false;
    baseSelectRef?.value?.buttonRef?.focus();
  }
};

onClickOutside(
  singleSelectRef,
  () => {
    isOpen.value = false;
  },
  {
    ignore: [buttonRef],
  },
);

onBeforeUpdate(() => {
  optionRefs.value = [];
});
</script>
